import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { FieldError, InputAdornment } from '@/components/glitch-ui/field-error';
import { useAtomValue } from 'jotai';
import { organizationDataAtom } from '@/store';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createApiKey, listApiKeys, revokeApiKey } from '@/lib/apis';
import { useToast } from '@/hooks/useToast';
import { Copy, Key, Plus, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { ConformationModal } from '@/components/glitch-ui/confirmation-modal';

interface ApiKeyResponse {
  id: number;
  key_prefix: string;
  name: string;
  scopes: string[];
  rate_limit_per_minute: number;
  is_active: boolean;
  expires_at: string | null;
  last_used_at: string | null;
  created_at: string;
}

interface ApiKeyCreateResponse {
  id: number;
  key: string;
  key_prefix: string;
  name: string;
  scopes: string[];
  created_at: string;
}

export const ApiKeysSettings = () => {
  const selectedOrganization = useAtomValue(organizationDataAtom);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [keyNameError, setKeyNameError] = useState<string | undefined>();
  const [createdKey, setCreatedKey] = useState<ApiKeyCreateResponse | null>(null);
  const [deleteKeyId, setDeleteKeyId] = useState<number | null>(null);

  const orgId = selectedOrganization?.id;

  const { data: keysData, isLoading } = useQuery({
    queryKey: ['api-keys', orgId],
    queryFn: () => listApiKeys(orgId!),
    enabled: !!orgId,
  });

  const createMutation = useMutation({
    mutationFn: (name: string) => createApiKey(orgId!, name),
    onSuccess: (data: ApiKeyCreateResponse) => {
      setCreatedKey(data);
      setShowCreateDialog(false);
      setNewKeyName('');
      queryClient.invalidateQueries({ queryKey: ['api-keys', orgId] });
    },
    onError: () => {
      toast({ title: 'Failed to create API key', variant: 'destructive' });
    },
  });

  const revokeMutation = useMutation({
    mutationFn: (keyId: number) => revokeApiKey(orgId!, keyId),
    onSuccess: () => {
      setDeleteKeyId(null);
      queryClient.invalidateQueries({ queryKey: ['api-keys', orgId] });
      toast({ title: 'API key revoked' });
    },
    onError: () => {
      toast({ title: 'Failed to revoke API key', variant: 'destructive' });
    },
  });

  // Tell-on-click validation: Create stays enabled while idle; clicking it marks
  // the missing field instead of sitting silently disabled.
  const handleCreateKey = () => {
    if (!newKeyName.trim()) {
      setKeyNameError('Required');
      return;
    }
    createMutation.mutate(newKeyName);
  };

  const handleCopyKey = (key: string) => {
    navigator.clipboard.writeText(key);
    toast({ title: 'API key copied to clipboard' });
  };

  const keys: ApiKeyResponse[] = keysData?.items ?? [];

  if (!orgId) {
    return <p className="text-gray-500">Select an organization to manage API keys.</p>;
  }

  return (
    <div className="space-y-6">
      <Card className="w-full !bg-transparent border-none shadow-none">
        <CardContent className="p-0 space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">API Keys</h2>
              <p className="text-sm text-gray-500 mt-1">
                Manage API keys for programmatic access to your organization.
              </p>
            </div>
            <Button onClick={() => setShowCreateDialog(true)} className="bg-[#290BD9] hover:bg-[#2309C5]">
              <Plus className="h-4 w-4 mr-2" />
              Create Key
            </Button>
          </div>

          {isLoading ? (
            <p className="text-gray-500 text-sm">Loading...</p>
          ) : keys.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Key className="h-10 w-10 text-gray-300 mb-3" />
                <p className="text-gray-900 font-medium">No API Keys</p>
                <p className="text-gray-500 text-sm mt-1">Create an API key to get started with the GlitchAds SDK and CLI.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {keys.map((apiKey) => (
                <Card key={apiKey.id}>
                  <CardContent className="flex items-center justify-between p-4">
                    <div className="flex items-center gap-3">
                      <Key className="h-4 w-4 text-gray-400" />
                      <div>
                        <p className="font-medium text-gray-900">{apiKey.name}</p>
                        <p className="text-sm text-gray-500 font-mono">{apiKey.key_prefix}{'*'.repeat(12)}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right text-sm text-gray-500">
                        {apiKey.last_used_at ? (
                          <span>Last used {new Date(apiKey.last_used_at).toLocaleDateString()}</span>
                        ) : (
                          <span>Never used</span>
                        )}
                      </div>
                      <button
                        onClick={() => setDeleteKeyId(apiKey.id)}
                        className="p-1.5 rounded text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Key Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={(open) => { setShowCreateDialog(open); setKeyNameError(undefined); }}>
        <DialogContent aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>Create API Key</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="key-name">Key Name</Label>
                <FieldError message={keyNameError} />
              </div>
              <div className="relative mt-1.5">
                <Input
                  id="key-name"
                  placeholder="e.g., CI Pipeline, Dashboard Integration"
                  value={newKeyName}
                  onChange={(e) => { setNewKeyName(e.target.value); setKeyNameError(undefined); }}
                  error={!!keyNameError}
                  className={keyNameError ? 'pr-8' : ''}
                />
                <InputAdornment error={!!keyNameError} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateKey}
              disabled={createMutation.isPending}
              className="bg-[#290BD9] hover:bg-[#2309C5]"
            >
              {createMutation.isPending ? 'Creating...' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Show Created Key Dialog */}
      <Dialog open={!!createdKey} onOpenChange={() => setCreatedKey(null)}>
        <DialogContent aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>API Key Created</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md">
              <p className="text-sm font-medium text-yellow-800">
                Copy this key now. It will not be shown again.
              </p>
            </div>
            <div>
              <Label>API Key</Label>
              <div className="flex items-center gap-2 mt-1.5">
                <Input
                  readOnly
                  value={createdKey?.key ?? ''}
                  className="font-mono text-sm"
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => createdKey && handleCopyKey(createdKey.key)}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setCreatedKey(null)} className="bg-[#290BD9] hover:bg-[#2309C5]">
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revoke Confirmation */}
      <ConformationModal
        isOpen={deleteKeyId !== null}
        onClose={() => setDeleteKeyId(null)}
        onConfirm={() => deleteKeyId && revokeMutation.mutate(deleteKeyId)}
        title="Revoke API Key"
        description="This API key will be permanently revoked. Any applications using this key will lose access immediately."
        confirmText={revokeMutation.isPending ? 'Revoking...' : 'Revoke Key'}
      />
    </div>
  );
};
